import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { createCostEstimate } from '#/api/crmMgmt/costEstimate/create';
import { fetchCostEstimateDetail } from '#/api/crmMgmt/costEstimate/fetchDetail';
import { updateCostEstimate } from '#/api/crmMgmt/costEstimate/update';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import CostEstimateLinesEditor from '#/modules/crmMgmt/costEstimate/components/CostEstimateLinesEditor';
import {
  getCostEstimateApiErrorMessage,
  isCostEstimateVersionConflict,
} from '#/modules/crmMgmt/costEstimate/helper/apiError';
import {
  buildCostEstimateCreateBody,
  buildCostEstimatePatchBody,
  mapCostEstimateDetailToFormValues,
} from '#/modules/crmMgmt/costEstimate/helper/costEstimateLines';
import {
  COST_ESTIMATE_FIELD_LABELS,
  costEstimateFormSchema,
  resolveCostEstimateFieldLabel,
} from '#/modules/crmMgmt/costEstimate/schemas/costEstimateSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import { ROUNDING_MODE_OPTIONS } from '#/utils/roundingMode';
import {
  costEstimateDetailQueryKey,
  costEstimateListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  name: '',
  roundingMode: 'HALF_UP',
  remark: '',
  lines: [],
};

const CreateEditCostEstimatePage = () => {
  const { id } = useParams();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: costEstimateDetailQueryKey(id),
    queryFn: () => fetchCostEstimateDetail(id),
    enabled: Boolean(id),
  });
  const detail = detailQuery.data?.data;

  async function saveCostEstimate(values) {
    if (!id) {
      return createCostEstimate(buildCostEstimateCreateBody(values));
    }
    return updateCostEstimate(id, buildCostEstimatePatchBody(values, detail?.record?.updatedAt));
  }

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    mutationFn: saveCostEstimate,
    listQueryKeyPrefix: costEstimateListQueryKeyPrefix,
    detailQueryKey: costEstimateDetailQueryKey,
    getDetailPath: ROUTES.costEstimateDetail,
    afterUpdateNavigateTo: id ? ROUTES.costEstimateDetail(id) : null,
    getErrorMessage: (error) => getCostEstimateApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isCostEstimateVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(costEstimateFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!detail) {
      return;
    }
    resetValidationSummary();
    reset(mapCostEstimateDetailToFormValues(detail));
  }, [detail, reset, resetValidationSummary]);

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯成本試算' : '新增成本試算';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const backTo = isEdit ? ROUTES.costEstimateDetail(id) : ROUTES.costEstimate;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <div className="flex items-center gap-2">
              <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
                <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={backTo}>{isEdit ? '返回詳情' : '返回列表'}</Link>
              </Button>
            </div>
          </div>

          {isPageLoading ? <p className="text-sm text-slate-500">載入中…</p> : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getCostEstimateApiErrorMessage(detailQuery.error, '找不到成本試算')}
            </p>
          ) : null}

          {!isPageLoading && !isDetailNotFound ? (
            <div className="space-y-4">
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={COST_ESTIMATE_FIELD_LABELS}
                fieldLabelResolver={resolveCostEstimateFieldLabel}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, markSubmitAttempted)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500 pb-0!">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">試算資訊</h2>
                    </div>
                    <div className={cn('grid grid-cols-1 gap-3', '@4xl/sheet:grid-cols-3')}>
                      <div className="@4xl/sheet:col-span-2">
                        <FloatingInput
                          id="name"
                          label="試算名稱"
                          register={register}
                          errors={errors}
                        />
                      </div>
                      <FloatingSelect
                        id="roundingMode"
                        label="總額取整"
                        options={ROUNDING_MODE_OPTIONS}
                        control={control}
                        errors={errors}
                        isClearable={false}
                      />
                      <div className="@4xl/sheet:col-span-3">
                        <FloatingTextarea
                          id="remark"
                          label="備註"
                          register={register}
                          errors={errors}
                        />
                      </div>
                    </div>
                  </section>

                  <CostEstimateLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    getValues={getValues}
                  />
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={backTo}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : (isEdit ? '儲存' : '建立')}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditCostEstimatePage;
